import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { Network } from '@capacitor/network';
import { BehaviorSubject, lastValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import Userback from "@userback/widget";

import {
    updateConnectionStatus,
    syncData
} from '../utils/states-management/actions/offline.action';
import { ConnectionState, selectIsSyncInProgress } from '../utils/states-management/reducers/offline.reducer';

interface ConnectionMetrics {
    latency: number;
    retryCount: number;
    offlineDuration: number;
}

@Injectable({
    providedIn: 'root'
})
export class ConnectionService {
    private connectionQualityChecker: any;
    private _reconnectionTimer: any = null;
    private connectionRetryCount = 0;
    private maxRetryAttempts = 3;
    private retryDelay = 30000;
    private goodConnectionCount = 0;
    private requiredGoodConnectionsToSync = 1;
    private lastConnectionQuality: 'good' | 'poor' | 'offline' = 'good';
    private checkRequested$ = new Subject<void>();
    private consecutiveChecks = 0;
    private requiredConsecutiveChecksToSync = 0;
    private baseCheckInterval = 10000;

    private _isOnline = new BehaviorSubject<boolean>(false);
    private syncDataSubscription: Subscription;
    private _connectionQuality = new BehaviorSubject<'good' | 'poor' | 'offline'>('offline');

    public isOnline$ = this._isOnline.asObservable();
    public connectionQuality$ = this._connectionQuality.asObservable();
    private offlineStartTime: number | null = null;
    private networkListener: any;

    private poorQualityAttempts = 0;
    private maxPoorQualityAttempts = 3;

    private connectionMetrics: ConnectionMetrics = {
        latency: 0,
        retryCount: 0,
        offlineDuration: 0
    };
    private isTestingConnection = new BehaviorSubject<boolean>(false);
    public connectionTesting$ = this.isTestingConnection.asObservable();
    private isTesting = false;
    private userBackWidgetInstance: any = null;

    constructor(
        private store: Store<{ connection: ConnectionState }>,
        private http: HttpClient
    ) {
        this.initNetworkListeners();
        this.checkRequested$.subscribe(() => {
            if (!this.isTesting) {
                this.startConnectionQualityMonitoring();
            }
        });
        this.connectionTesting$.subscribe(isTesting => {
            this.isTesting = isTesting;
        })

    }

    private updateConnectionMetrics(latency?: number): void {
        if (latency !== undefined) {
            this.connectionMetrics.latency = latency;
        }
        this.connectionMetrics.retryCount = this.connectionRetryCount;
        this.connectionMetrics.offlineDuration = this.offlineStartTime
            ? Date.now() - this.offlineStartTime
            : 0;
    }

    public initNetworkListeners(msg = '[Connection] Initial app startup connection check'): void {
        const safeNetworkCheck = async () => {
            try {
                const status = await Network.getStatus();
                if (!status.connected) {
                    this.stopConnectionQualityMonitoring();
                    this.scheduleReconnection();
                    this.updateConnectionState(false, 'offline');
                    return;
                }
                if (status.connected && !this.connectionQualityChecker) {
                    if (!this.isTesting) {
                        console.log('############### initNetworkListeners ###############');
                        this.startConnectionQualityMonitoring();
                    }
                }
            } catch (error) {
                console.error('Network status check failed:', error);
                this.updateConnectionState(false, 'offline');
            }
        };

        safeNetworkCheck();

        this.networkListener = Network.addListener('networkStatusChange', (status) => {
            try {
                console.log(`Network status changed: connected=${status.connected}`);
                this.updateConnectionState(status.connected, status.connected ? 'good' : 'offline');

                if (status.connected) {
                    this.restartMonitoring(msg);
                } else {
                    this.stopConnectionQualityMonitoring();
                }
            } catch (error) {
                console.error('Network status handler error:', error);
                this.updateConnectionState(false, 'offline');
            }
        });
    }

    public requestConnectionCheck(): void {
        this.checkRequested$.next();
    }
    public isTestingConnectionChange(isTestingConnection): void {
        this.isTestingConnection.next(isTestingConnection);
    }

    private async updateConnectionState(isOnline: boolean, quality?: 'good' | 'poor' | 'offline'): Promise<void> {
        const connectionQuality = quality;
        if (this._isOnline.value === isOnline && this._connectionQuality.value === connectionQuality) {
            return;
        }
        this.store.dispatch(updateConnectionStatus({ isOnline, connectionQuality }));

        // Add state transition logging
        console.log(`Connection state transition: 
            ${this._isOnline.value ? 'Online' : 'Offline'} => ${isOnline ? 'Online' : 'Offline'},
            Quality: ${this._connectionQuality.value} => ${connectionQuality}`);

        // Actually update the state values - this was missing!
        this._isOnline.next(isOnline);
        this._connectionQuality.next(connectionQuality);

        // Add emergency cache clearance for critical state changes
        if (this.lastConnectionQuality === 'good' && connectionQuality === 'offline') {
            localStorage.removeItem('pendingAppVersion');
            sessionStorage.removeItem('tempSessionData');
        }

        this.lastConnectionQuality = connectionQuality;
    }

    private startConnectionQualityMonitoring(): void {
        this.stopConnectionQualityMonitoring();
        this.isTestingConnectionChange(true)
        this.goodConnectionCount = 0;
        this.connectionRetryCount = 0;
        this.consecutiveChecks = 0;


        if (!this.connectionQualityChecker) {
            console.log('[Connection] First start Monitoring ');
            this.checkConnectionQuality();
        }
        else {
            return;
        }

        this.connectionQualityChecker = setInterval(() => {
            console.log('[Connection] Starting quality monitoring with interval:', this.baseCheckInterval);
            this.checkConnectionQuality();
        }, this.baseCheckInterval);
    }

    private handlePoorConnection(): void {
        console.log(`[Connection] Poor connection attempt ${this.poorQualityAttempts}/${this.maxPoorQualityAttempts}`);
        this.goodConnectionCount = 0;
        if (this.poorQualityAttempts >= this.maxPoorQualityAttempts) {

            this.stopConnectionQualityMonitoring('[Connection Monitoring Stopped] # Max poor quality attempts reached - stopping monitoring ! #');
            return;
        }
        this.poorQualityAttempts++;
        this.consecutiveChecks = 0;
    }

    private async checkServiceWorkerState(): Promise<boolean> {
        if (!('serviceWorker' in navigator)) return true;

        try {
            const registration = await navigator.serviceWorker.getRegistration();
            return registration?.active?.state === 'activated' &&
                registration?.waiting === undefined;
        } catch (error) {
            console.error('Service Worker check failed:', error);
            return false;
        }
    }

    private async verifyCacheCompleteness(): Promise<boolean> {
        if (!('caches' in window)) return true;

        try {
            const requiredCaches = await caches.keys();
            const cacheValidation = await Promise.all(
                requiredCaches.map(async cacheName => {
                    const cache = await caches.open(cacheName);
                    const requests = await cache.keys();
                    return requests.length > 0;
                })
            );
            return cacheValidation.every(Boolean);
        } catch (error) {
            console.error('Cache verification failed:', error);
            return false;
        }
    }

    public async checkConnectionQuality(): Promise<void> {
        try {
            console.log('[Connection] Starting quality check', {
                retryCount: this.connectionRetryCount,
                lastLatency: this.connectionMetrics.latency
            });

            // Simplify this to avoid potential issues with Promise.all
            const status = await Network.getStatus();
            console.log(status)
            if (!status.connected) {
                // this.store.dispatch(updateConnectionStatus({ isOnline: status.connected, connectionQuality: 'offline' }));

                this.updateConnectionState(false, 'offline');
                this.stopConnectionQualityMonitoring('[Connection Monitoring Stopped] Offline detected during quality check');
                this.scheduleReconnection();
                return;
            }

            const startTime = Date.now();
            const controller = new AbortController();
            const signal = controller.signal;

            // const poorTimer = setTimeout(() => {
            //     console.log('[Connection] Response time >4000ms - marking as poor');
            //     this.updateConnectionState(true, 'poor');
            // }, 4000);

            // const offlineTimer = setTimeout(() => {
            //     console.log('[Connection] Response time >10000ms - marking as offline');
            //     controller.abort(); // Cancel the request
            //     this.updateConnectionState(false, 'offline');
            //     this.handleConnectionFailure();
            // }, 10000);

            this.http.get('https://speed.cloudflare.com/__down?bytes=10000', {
                observe: 'response',
                responseType: 'text'
            }).pipe(take(1)).subscribe({
                next: async (response) => {
                    // clearTimeout(poorTimer);
                    // clearTimeout(offlineTimer);

                    const latency = Date.now() - startTime;

                    let connectionQuality: 'good' | 'poor' | 'offline' = 'offline';
                    if (response.status === 200) {
                        connectionQuality = latency < 1500 ? 'good' : latency < 5000 ? 'poor' : 'offline';
                    }

                    console.log(`[Connection] Quality assessment: ${connectionQuality.toUpperCase()} (${latency}ms)`, {
                        status: response.status,
                        latency,
                        consecutiveChecks: this.consecutiveChecks
                    });

                    const connectionState = await lastValueFrom(this.store.select(('connection')).pipe(take(1)));

                    if (connectionQuality === 'good' || connectionQuality === 'poor') {
                        this.updateConnectionState(true, connectionQuality);

                        console.log(`[Connection] Connection (${connectionQuality}) detected to sync (${this.consecutiveChecks}/${this.requiredConsecutiveChecksToSync})`);
                        let isAppSync = await lastValueFrom(
                            this.store.select(selectIsSyncInProgress).pipe(take(1))
                        );
                        if (this.consecutiveChecks == this.requiredConsecutiveChecksToSync) {
                            this.handleSyncData(connectionState);
                        } else {
                            this.consecutiveChecks++;
                        }

                        if (connectionQuality === 'poor') {
                            this.handlePoorConnection();
                        } else if (connectionQuality === 'good') {
                            this.handleGoodConnection();
                        }


                    } else {
                        console.log('[Connection] Connection test failed !');
                        this.updateConnectionState(false, connectionQuality);
                        this.handleConnectionFailure();

                    }

                },
                error: (err) => {

                    if (err.name === 'AbortError') {
                        console.log('[Connection] Request aborted due to timeout');
                        return;
                    }

                    console.error('[Connection] HTTP request failed:', err);
                    this.updateConnectionState(false, 'offline');
                    this.handleConnectionFailure();
                }
            });


        } catch (error) {
            console.error('Connection check error:', error);

        }
    }

    public async handleSyncData(connectionState): Promise<void> {
        if (Object.keys(connectionState.entities).length > 0
        ) {
            console.log('[Connection] Found existing unsynced data - sync');

            this.store.dispatch(syncData());

        }
    }

    public async handleGoodConnection(): Promise<void> {
        this.poorQualityAttempts = 0;
        this.connectionRetryCount = 0;
        console.log(`[Connection] Good connection attempt ${this.goodConnectionCount}/${this.requiredGoodConnectionsToSync}`);

        if (this.goodConnectionCount == this.requiredGoodConnectionsToSync) {
            this.stopConnectionQualityMonitoring('[Connection Monitoring Stopped] Required good connections reached');
        } else {
            this.goodConnectionCount++;
        }
    }

    public restartMonitoring(msg = '[Connection Monitoring Stopped] restart Monitoring'): void {
        this.poorQualityAttempts = 0;
        this.connectionRetryCount = 0;
        this.offlineStartTime = null;
        this.stopConnectionQualityMonitoring(msg);

        if (!this.connectionQualityChecker) {

            console.log('############### restartMonitoring ###############');

            this.startConnectionQualityMonitoring();
        }
    }


    private handleConnectionFailure(): void {
        console.log('[Connection] Handling connection failure', {
            retryCount: this.connectionRetryCount,
            maxAttempts: this.maxRetryAttempts
        });

        if (this.connectionRetryCount < this.maxRetryAttempts) {
            this.connectionRetryCount++;
        }

        this.goodConnectionCount = 0;
        this.retryDelay = this.calculateRetryDelay();

        console.log(`[Connection] New retry delay: ${this.retryDelay}ms`);
        this.updateConnectionMetrics();

        if (this.connectionRetryCount === 1) {
            console.log('[Connection] Starting offline duration timer');
            this.offlineStartTime = Date.now();
        }

        const offlineDuration = this.offlineStartTime ? Date.now() - this.offlineStartTime : 0;
        if (offlineDuration > 120000) {
            console.log('[Connection] Extended offline duration detected');
            this.stopConnectionQualityMonitoring('[Connection Monitoring Stopped] Offline for 2+ minutes');
            return;
        }

        if (this.connectionRetryCount <= this.maxRetryAttempts) {
            console.log(`[Connection] Scheduling retry ${this.connectionRetryCount}/${this.maxRetryAttempts}`);
            this.scheduleReconnection();
        } else {
            console.log('[Connection] Maximum retry attempts reached');
            this.stopConnectionQualityMonitoring('[Connection Monitoring Stopped] No more retries');
        }
    }

    stopConnectionQualityMonitoring(message = '[Connection] Stopping quality monitoring'): void {
        if (this.connectionQualityChecker) {
            console.log(message);
            clearInterval(this.connectionQualityChecker);
            this.connectionQualityChecker = null;
            this.isTestingConnectionChange(false)
        } else {
            console.log('[Connection] Stop requested but no monitoring active');
        }
    }

    private scheduleReconnection(): void {
        console.log('[Connection] Scheduling reconnection attempt', {
            delay: this.retryDelay,
            nextAttempt: new Date(Date.now() + this.retryDelay).toISOString()
        });

        if (this._reconnectionTimer) {
            console.log('[Connection] Clearing existing reconnection timer');
            clearTimeout(this._reconnectionTimer);
        }

        this._reconnectionTimer = setTimeout(async () => {
            console.log('[Connection] ******************* Executing scheduled reconnection check *******************');
            await Network.getStatus().then(status => {
                if (status.connected) {
                    if (!this.isTesting) {
                        this.startConnectionQualityMonitoring();
                    }
                }

            });
        }, this.retryDelay);
    }


    private cleanupResources(): void {
        this.stopConnectionQualityMonitoring('[Connection Monitoring Stopped] Cleanup Resources');

        if (this._reconnectionTimer) {
            clearTimeout(this._reconnectionTimer);
            this._reconnectionTimer = null;
        }
    }
    private calculateRetryDelay(): number {
        return Math.min(30000, 5000 * Math.pow(2, this.connectionRetryCount - 1));
    }

    public ngOnDestroy(): void {
        this.cleanupResources();
        this._isOnline.complete();
        this._connectionQuality.complete();
        this.checkRequested$.complete();
    }
}

